import {useEffect, useRef, useState} from 'react'
import {useDebounce} from '@/hooks/use-debounce'
import {checkUsernameAvailability} from '@/services/user-services'

export type UsernameAvailabilityStatus =
    | 'idle'
    | 'checking'
    | 'available'
    | 'unavailable'
    | 'error'

export interface UsernameAvailabilityState {
    status: UsernameAvailabilityStatus
    checkedUsername: string | null
}

const MIN_LENGTH_FOR_LOOKUP = 2
const DEBOUNCE_MS = 300

export function useUsernameAvailability(
    username: string,
    token: string | null | undefined,
): UsernameAvailabilityState {
    const debouncedUsername = useDebounce(username, DEBOUNCE_MS)
    const [state, setState] = useState<UsernameAvailabilityState>({
        status: 'idle',
        checkedUsername: null,
    })
    const abortRef = useRef<AbortController | null>(null)
    const latestRawTrimmedRef = useRef(username.trim())

    useEffect(() => {
        latestRawTrimmedRef.current = username.trim()
    })

    useEffect(() => {
        const trimmed = debouncedUsername.trim()

        abortRef.current?.abort()

        if (!token || trimmed.length < MIN_LENGTH_FOR_LOOKUP) {
            setState({status: 'idle', checkedUsername: null})
            return
        }

        const controller = new AbortController()
        abortRef.current = controller
        setState({status: 'checking', checkedUsername: trimmed})

        checkUsernameAvailability(trimmed, token, controller.signal)
            .then((result) => {
                if (controller.signal.aborted || !result) return
                if (latestRawTrimmedRef.current !== trimmed) return
                setState({
                    status: result.available ? 'available' : 'unavailable',
                    checkedUsername: trimmed,
                })
            })
            .catch((reason) => {
                if (controller.signal.aborted) return
                if (reason?.name === 'AbortError') return
                if (latestRawTrimmedRef.current !== trimmed) return
                setState({status: 'error', checkedUsername: trimmed})
            })

        return () => {
            controller.abort()
        }
    }, [debouncedUsername, token])

    return state
}
